import { useState } from "react";
import { useTranslation } from "react-i18next";

import { RECALL_TYPE } from "components/Recalls";
import { OpenRecallRow, RecallHeader } from "components/Recalls/components/RecallsModal/components";
import "components/Recalls/components/RecallsModal/components/OpenRecalls/OpenRecalls.scss";
import { retrieveOpenRecalls } from "components/Recalls/utils";

import type { RecallEntity } from "models";
import type { AddInterventionPayload } from "modules/AppointmentDetails/hooks";

type OpenRecallsProps = {
  selectOnly: boolean;
  rdwRecalls?: RecallEntity[];
  ponRecalls?: RecallEntity[];
  interventions?: AddInterventionPayload[];
  distrimateRecalls?: RecallEntity[];
  isPONRecallConverted: (recall: RecallEntity) => boolean;
  isDistrimateOrRDWRecallConverted: (recall: RecallEntity) => boolean;
  onConvertRecallToIntervention: (recalls: RecallEntity, recallType: RECALL_TYPE) => void;
  onSelectAllRecalls?: (checked: boolean) => void;
  handleSelectionChange?: (recall: RecallEntity, recallType: RECALL_TYPE) => void;
};

export const OpenRecalls = ({
  rdwRecalls,
  ponRecalls,
  selectOnly,
  interventions,
  distrimateRecalls,
  onSelectAllRecalls,
  isPONRecallConverted,
  handleSelectionChange,
  onConvertRecallToIntervention,
  isDistrimateOrRDWRecallConverted
}: OpenRecallsProps) => {
  const { t } = useTranslation();

  const openDistrimateRecalls = retrieveOpenRecalls(distrimateRecalls);
  const openRDWRecalls = retrieveOpenRecalls(rdwRecalls);

  if (!openDistrimateRecalls.length && !ponRecalls?.length && !openRDWRecalls.length) return null;

  const isAllOptionPreselected = [...openDistrimateRecalls, ...(ponRecalls || []), ...openRDWRecalls].every(recall =>
    interventions?.some(intervention => [intervention.recalls_id, intervention.pon_recall_id, intervention.rdw_recall_id].includes(recall.id))
  );

  const [allOptionSelected, setAllOptionSelected] = useState(isAllOptionPreselected);
  const toggleAllOption = () => setAllOptionSelected(curr => !curr);
  const unselectAllOption = () => setAllOptionSelected(false);

  return (
    <div className={`OpenRecalls OpenRecalls-${selectOnly ? "select" : "convert"}`}>
      {openRDWRecalls.length ? (
        <>
          <RecallHeader
            onSelectAllRecalls={onSelectAllRecalls}
            title={t("v8_open_rdw_recalls", "Open RDW Recalls")}
            showColumnNames={true}
            selectOnly={selectOnly}
            allOptionSelected={allOptionSelected}
            isAllOptionPreselected={isAllOptionPreselected}
            toggleAllOption={toggleAllOption}
          />
          {openRDWRecalls.map(recall => (
            <OpenRecallRow
              key={recall.id}
              label={`#${recall.id}  ${recall.campaign_name}`}
              isConvertButtonDisabled={isDistrimateOrRDWRecallConverted(recall)}
              status={recall.status}
              endDate={recall.close_date}
              onConvertToIntervention={() => onConvertRecallToIntervention(recall, RECALL_TYPE.RDWRecall)}
              selectOnly={selectOnly}
              handleSelectionChange={handleSelectionChange}
              recall={recall}
              recallType={RECALL_TYPE.RDWRecall}
              interventions={interventions}
              unselectAllOption={unselectAllOption}
              allOptionSelected={allOptionSelected}
            />
          ))}
        </>
      ) : null}

      {openDistrimateRecalls.length || ponRecalls?.length ? (
        <>
          {
            <RecallHeader
              title={t("v8_open_factory_recalls", "Open Factory Recalls")}
              showColumnNames={openRDWRecalls.length === 0}
              selectOnly={selectOnly}
              onSelectAllRecalls={onSelectAllRecalls}
              allOptionSelected={allOptionSelected}
              isAllOptionPreselected={isAllOptionPreselected}
              toggleAllOption={toggleAllOption}
            />
          }
          {openDistrimateRecalls.map(recall => {
            return (
              <OpenRecallRow
                key={recall.id}
                onConvertToIntervention={() => onConvertRecallToIntervention(recall, RECALL_TYPE.DistrimateRecall)}
                isConvertButtonDisabled={isDistrimateOrRDWRecallConverted(recall)}
                status={recall.status}
                endDate={recall.close_date}
                label={`#${recall.campaign_code}  ${recall.campaign_name}`}
                selectOnly={selectOnly}
                handleSelectionChange={handleSelectionChange}
                recall={recall}
                recallType={RECALL_TYPE.DistrimateRecall}
                interventions={interventions}
                unselectAllOption={unselectAllOption}
                allOptionSelected={allOptionSelected}
              />
            );
          })}
          {ponRecalls?.map(recall => (
            <OpenRecallRow
              key={recall.id}
              isConvertButtonDisabled={isPONRecallConverted(recall)}
              label={`#${recall.id}  ${recall.campaign_name}`}
              onConvertToIntervention={() => onConvertRecallToIntervention(recall, RECALL_TYPE.PonRecall)}
              selectOnly={selectOnly}
              handleSelectionChange={handleSelectionChange}
              recall={recall}
              recallType={RECALL_TYPE.PonRecall}
              interventions={interventions}
              unselectAllOption={unselectAllOption}
              allOptionSelected={allOptionSelected}
            />
          ))}
        </>
      ) : null}
    </div>
  );
};
